import { useMemo } from 'react';

import { hasQuestionnaireContent } from './launchGuard';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { useGetCycleMilestonesByCycle } from '@/api/cycle-evaluation-services/cycle-milestone';
import {
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUserPermissions,
} from '@/api/directory';
import { CycleAudienceType, EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { buildAudienceFilterParams } from '@/pages/CycleEvaluation/CycleInformation/audienceFilterParams';
import { getCheckedEvaluationTypes } from '@/pages/CycleEvaluation/Deadlines/milestones';
import { getCompanyAttributeList } from '@/utils/company';
import { transformCriteriaToFilters } from '@/utils/cycleEvaluation';

const AUDIENCE_COUNT_PAGE_SIZE = 1;

/**
 * All the LaunchSummary page's raw data-fetching, in one place: the group, its orphan
 * milestones, the company/permissions context, and the per-review-type evaluation queries —
 * plus the resolved employee count and the "is anything still loading" flag they all feed.
 */
export const useLaunchSummaryQueries = (cycleEvaluationGroupId: string | undefined) => {
    const {
        data: group,
        isError: isGroupError,
        isPending: isGroupPending,
    } = useGetCycleEvaluationGroup(cycleEvaluationGroupId);

    const {
        data: orphanMilestonesData,
        isError: isMilestonesError,
        isPending: isMilestonesPending,
    } = useGetCycleMilestonesByCycle(cycleEvaluationGroupId);

    const orphanMilestones = orphanMilestonesData ?? [];

    const {
        data: companyInformation,
        isError: isCompanyInformationError,
        isPending: isCompanyInformationPending,
    } = useGetCompanyInformation();

    const { data: permissions, isError: isPermissionsError } = useGetUserPermissions();

    const checkedTypes = useMemo(() => getCheckedEvaluationTypes(group), [group]);

    const selfEvaluationChild = (group?.cycleEvaluations ?? []).find(
        (evaluation) => evaluation.reviewType === EvaluationType.SELF_EVALUATION,
    );
    const feedback360Child = (group?.cycleEvaluations ?? []).find(
        (evaluation) => evaluation.reviewType === EvaluationType.EVALUATION_360,
    );
    const managerEvaluationChild = (group?.cycleEvaluations ?? []).find(
        (evaluation) => evaluation.reviewType === EvaluationType.MANAGER_EVALUATION,
    );

    const selfEvaluationQuery = useGetCycleEvaluation(selfEvaluationChild?.id ?? '');
    const feedback360Query = useGetCycleEvaluation(feedback360Child?.id ?? '');
    const managerEvaluationQuery = useGetCycleEvaluation(managerEvaluationChild?.id ?? '');

    const cycleEvaluationByType: Partial<
        Record<EvaluationTypeValue, typeof selfEvaluationQuery.data>
    > = useMemo(
        () => ({
            [EvaluationType.SELF_EVALUATION]: selfEvaluationQuery.data,
            [EvaluationType.EVALUATION_360]: feedback360Query.data,
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery.data,
        }),
        [selfEvaluationQuery.data, feedback360Query.data, managerEvaluationQuery.data],
    );

    const filterParams = useMemo(() => {
        const { filters } = transformCriteriaToFilters({
            criterias: group?.audience?.cycleAudienceCriterias ?? [],
            companyAttributes: getCompanyAttributeList(companyInformation),
        });

        return buildAudienceFilterParams({
            audienceType: group?.audience?.cycleAudienceType ?? CycleAudienceType.ENTIRE_COMPANY,
            filters,
        });
    }, [group, companyInformation]);

    const { data: companyPeopleData, isError: isCompanyPeopleError } = useGetCompanyPeople(
        {
            pageNumber: 1,
            pageSize: AUDIENCE_COUNT_PAGE_SIZE,
            isActiveOnly: true,
            ...filterParams,
        },
        { canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments },
    );

    const resolvedEmployeeCount = companyPeopleData?.totalCount ?? 0;

    const questionnairePendingByType: Partial<Record<EvaluationTypeValue, boolean>> = useMemo(
        () => ({
            [EvaluationType.SELF_EVALUATION]: selfEvaluationQuery.isPending,
            [EvaluationType.EVALUATION_360]: feedback360Query.isPending,
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery.isPending,
        }),
        [
            selfEvaluationQuery.isPending,
            feedback360Query.isPending,
            managerEvaluationQuery.isPending,
        ],
    );

    const evaluationErrorByType: Partial<Record<EvaluationTypeValue, boolean>> = useMemo(
        () => ({
            [EvaluationType.SELF_EVALUATION]: selfEvaluationQuery.isError,
            [EvaluationType.EVALUATION_360]: feedback360Query.isError,
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery.isError,
        }),
        [selfEvaluationQuery.isError, feedback360Query.isError, managerEvaluationQuery.isError],
    );

    const isCycleEvaluationError = checkedTypes.some((type) => !!evaluationErrorByType[type]);

    // An evaluation that never loaded says NOTHING about its questionnaire. Reading its absent
    // questions as "no questionnaire content" turns a fetch failure into a launch blocked for a
    // reason the administrator cannot act on; the failure itself is what the error handler reports.
    const hasQuestionnaireContentByType: Partial<Record<EvaluationTypeValue, boolean>> = useMemo(
        () =>
            Object.fromEntries(
                checkedTypes.map((type) => {
                    if (questionnairePendingByType[type] || evaluationErrorByType[type]) {
                        return [type, true];
                    }

                    const cycleQuestionnaire = cycleEvaluationByType[type]?.cycleQuestionnaire;

                    return [
                        type,
                        hasQuestionnaireContent({
                            questionnaire: cycleQuestionnaire,
                            reviewType: type,
                        }),
                    ];
                }),
            ),
        [checkedTypes, cycleEvaluationByType, questionnairePendingByType, evaluationErrorByType],
    );

    const isLoading =
        isGroupPending ||
        isMilestonesPending ||
        isCompanyInformationPending ||
        (checkedTypes.includes(EvaluationType.SELF_EVALUATION) && selfEvaluationQuery.isPending) ||
        (checkedTypes.includes(EvaluationType.EVALUATION_360) && feedback360Query.isPending) ||
        (checkedTypes.includes(EvaluationType.MANAGER_EVALUATION) &&
            managerEvaluationQuery.isPending);

    return {
        group,
        isGroupError,
        orphanMilestones,
        isMilestonesError,
        isCompanyInformationError,
        isCompanyPeopleError,
        isPermissionsError,
        isCycleEvaluationError,
        checkedTypes,
        cycleEvaluationByType,
        hasQuestionnaireContentByType,
        resolvedEmployeeCount,
        isLoading,
    };
};
